<template>
  <aside
    class="my-6 lg:my-10"
    :aria-label="heading"
    :data-testid="dataTestid"
  >
    <slot name="header">
      <p class="mb-3 text-sm font-semibold text-semantic-surface-on-surface">
        {{ heading }}
      </p>
    </slot>

    <ul class="flex flex-wrap gap-2 list-none p-0 m-0">
      <li
        v-for="provider in providers"
        :key="provider.key"
        class="!list-none !p-0 !m-0 before:!hidden"
      >
        <NuxtLink
          class="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white [transition:transform_.2s_ease,opacity_.15s_ease-in-out] hover:opacity-[0.88] hover:scale-[1.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current active:scale-[0.98]"
          :class="provider.color"
          :aria-label="`Ask ${provider.label} to summarise this article`"
          :to="provider.href"
          target="_blank"
          rel="noopener noreferrer"
          @click="handleTracking(provider)"
        >
          <img
            v-if="provider.icon"
            :src="provider.icon"
            :alt="provider.label"
            class="w-4 h-4"
            aria-hidden="true"
          />

          <span>
            {{ provider.label }}
          </span>
        </NuxtLink>
      </li>
    </ul>
  </aside>
</template>

<script setup>
import {
  VERTICAL_LLM_TOPIC
} from '~/constants/pages/contentful.js'

defineOptions({
  name: 'V2MoleculesShareButtonsLlm'
})

const props = defineProps({
  dataTestid: {
    type: String,
    default: 'share-buttons-llm'
  },

  url: {
    type: String,
    default: ''
  },

  topic: {
    type: String,
    default: ''
  },

  heading: {
    type: String,
    default: 'Read this article with AI:'
  },

  postType: {
    type: String,
    default: '',
    validator: value => (['', 'Guides', 'Articles', 'Expert Analysis', 'Media'].includes(value))
  },

  vertical: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['share'])

const { $tracking } = useNuxtApp()
const route = useRoute()

const pageUrl = computed(() => props.url || `${CC_DOMAIN}${route.path}`)

const prompt = computed(() =>
  `
    Provide a comprehensive summary of this article from Compare Club at ${pageUrl.value}.\nHighlight the main points, key insights, and conclusions in an accessible way. Please respond in English.
  `.trim()
)

const pageTopic = computed(() => {
  if (props.topic) {
    return props.topic
  }

  return getKey(props.vertical, {
    ...VERTICAL_LLM_TOPIC,
    default: 'Australian consumer finance and insurance'
  })
})

const providers = computed(() => {
  const q = encodeURIComponent(prompt.value)
  return [
    {
      key: 'chatgpt',
      label: 'ChatGPT',
      color: 'bg-[#10a37f]',
      icon: '/icons/ai/chatgpt.webp',
      href: `https://chatgpt.com/?q=${q}`
    },
    {
      key: 'claude',
      label: 'Claude',
      color: 'bg-[#c93c3c]',
      icon: '/icons/ai/claude.webp',
      href: `https://claude.ai/new?q=${q}`
    },
    {
      key: 'perplexity',
      label: 'Perplexity',
      color: 'bg-[#7a49f3]',
      icon: '/icons/ai/perplexity.webp',
      href: `https://www.perplexity.ai/search/new?q=${q}`
    },
    {
      key: 'grok',
      label: 'Grok',
      color: 'bg-[#111111]',
      icon: '/icons/ai/grok.webp',
      href: `https://x.com/i/grok?text=${q}`
    },
    {
      key: 'googleai',
      label: 'AI Mode',
      color: 'bg-[#4285f4]',
      icon: '/icons/ai/google.webp',
      href: `https://www.google.com/search?udm=50&aep=11&q=${q}`
    }
  ]
})

function handleTracking (provider) {
  const payload = {
    llm_platform: provider.key,
    page_url: pageUrl.value,
    page_type: `${props.postType}`.trim().toLowerCase(),
    page_topic: pageTopic.value
  }

  if ($tracking) {
    $tracking.customEvent('llm_share_click', payload)
  }

  emit('share', payload)
}
</script>
